import { useEffect } from "react";

type Importer = () => Promise<unknown>;

/**
 * Feature bundles behind the primary sidebar destinations. Importing the
 * same module specifier App.tsx uses for its lazy routes resolves to the
 * same Vite chunk, so warming these during idle time makes the first click
 * on any nav item render instantly instead of waiting on a network fetch.
 */
export const NAV_ROUTE_IMPORTERS: ReadonlyArray<Importer> = [
  () => import("@/features/dashboard"),
  () => import("@/features/partners"),
  () => import("@/features/employers"),
  () => import("@/features/groups"),
  () => import("@/features/divisions"),
  () => import("@/features/locations"),
  () => import("@/features/members"),
  () => import("@/features/hsa-members"),
  () => import("@/features/users"),
  () => import("@/features/customers"),
  () => import("@/features/invoices"),
  () => import("@/features/payments"),
  () => import("@/features/billing-preferences"),
  () => import("@/features/reports"),
  () => import("@/features/reconciliation"),
];

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

/** Data-saver or 2G connections should never pay for speculative chunks. */
function connectionAllowsPrefetch(): boolean {
  const connection = (navigator as { connection?: NetworkInformationLike }).connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !/(^|-)2g$/.test(connection.effectiveType ?? "");
}

/**
 * Sequentially imports the given chunks during browser idle time, one per
 * idle callback so prefetching never competes with user interaction.
 * Returns a cancel function; import failures are swallowed (prefetch is
 * purely speculative — the route's own lazy() still loads on demand).
 */
export function idlePrefetch(importers: ReadonlyArray<Importer>): () => void {
  let cancelled = false;
  let index = 0;

  const schedule = (work: () => void) => {
    if (typeof requestIdleCallback === "function") requestIdleCallback(() => work());
    else setTimeout(work, 300);
  };

  const step = () => {
    if (cancelled || index >= importers.length) return;
    const importer = importers[index];
    index += 1;
    void Promise.resolve()
      .then(importer)
      .catch(() => undefined)
      .finally(() => schedule(step));
  };

  schedule(step);
  return () => {
    cancelled = true;
  };
}

/**
 * Warm the nav route chunks once the authenticated shell mounts. No-ops
 * under vitest (importing every feature would slow the suite) and on
 * data-saver / 2G connections.
 */
export function useIdleRoutePrefetch(
  importers: ReadonlyArray<Importer> = NAV_ROUTE_IMPORTERS
): void {
  useEffect(() => {
    if (import.meta.env.MODE === "test") return undefined;
    if (!connectionAllowsPrefetch()) return undefined;
    return idlePrefetch(importers);
  }, [importers]);
}
